import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';

const KycOnboarding = () => {
  const { token } = useParams();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [form, setForm] = useState({
    name: '', document: '', email: '', birthDate: '', phone: '', postalCode: '', street: '', number: '', complement: '', district: '', city: '', state: '', occupation: '', monthlyIncome: '', sourceOfFunds: '', pepDeclared: false, termsAccepted: false,
  });

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post('/onboarding/accept', {
        token,
        name: form.name,
        document: form.document.replace(/\D/g, ''),
        email: form.email,
        birthDate: form.birthDate || null,
        phone: form.phone,
        postalCode: form.postalCode.replace(/\D/g, ''),
        street: form.street,
        number: form.number,
        complement: form.complement || null,
        district: form.district,
        city: form.city,
        state: form.state.toUpperCase(),
        occupation: form.occupation || null,
        monthlyIncome: form.monthlyIncome ? Number(form.monthlyIncome) : null,
        sourceOfFunds: form.sourceOfFunds || null,
        pepDeclared: form.pepDeclared,
        termsAccepted: form.termsAccepted,
      });
      setCompleted(true);
    } catch (error) {
      toast({ variant: 'destructive', title: 'Não foi possível concluir o cadastro', description: error.response?.data?.detail || 'Revise os dados e tente novamente.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (completed) {
    return <div className="min-h-screen bg-muted/30 p-6 flex items-center justify-center"><Card className="w-full max-w-xl"><CardHeader><CardTitle>Cadastro enviado para análise</CardTitle><CardDescription>Seus dados foram recebidos. O KYC está pendente de validação e a conta só poderá ser criada após aprovação.</CardDescription></CardHeader></Card></div>;
  }

  return (
    <div className="min-h-screen bg-muted/30 p-6 flex items-center justify-center">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle>Onboarding e KYC</CardTitle>
          <CardDescription>Etapa {step} de 3 · Identificação, endereço e qualificação cadastral.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {step === 1 && <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2"><Label>Nome completo</Label><Input value={form.name} onChange={(e) => update('name', e.target.value)} /></div>
            <div><Label>CPF/CNPJ</Label><Input value={form.document} onChange={(e) => update('document', e.target.value)} /></div>
            <div><Label>Data de nascimento</Label><Input type="date" value={form.birthDate} onChange={(e) => update('birthDate', e.target.value)} /></div>
            <div><Label>E-mail</Label><Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} /></div>
            <div><Label>Telefone</Label><Input value={form.phone} onChange={(e) => update('phone', e.target.value)} /></div>
          </div>}

          {step === 2 && <div className="grid gap-4 md:grid-cols-2">
            <div><Label>CEP</Label><Input value={form.postalCode} onChange={(e) => update('postalCode', e.target.value)} /></div>
            <div><Label>UF</Label><Input maxLength={2} value={form.state} onChange={(e) => update('state', e.target.value)} /></div>
            <div className="md:col-span-2"><Label>Endereço</Label><Input value={form.street} onChange={(e) => update('street', e.target.value)} /></div>
            <div><Label>Número</Label><Input value={form.number} onChange={(e) => update('number', e.target.value)} /></div>
            <div><Label>Complemento</Label><Input value={form.complement} onChange={(e) => update('complement', e.target.value)} /></div>
            <div><Label>Bairro</Label><Input value={form.district} onChange={(e) => update('district', e.target.value)} /></div>
            <div><Label>Cidade</Label><Input value={form.city} onChange={(e) => update('city', e.target.value)} /></div>
          </div>}

          {step === 3 && <div className="space-y-4">
            <div><Label>Profissão / atividade</Label><Input value={form.occupation} onChange={(e) => update('occupation', e.target.value)} /></div>
            <div><Label>Renda mensal aproximada</Label><Input type="number" min="0" step="0.01" value={form.monthlyIncome} onChange={(e) => update('monthlyIncome', e.target.value)} /></div>
            <div><Label>Origem principal dos recursos</Label><Input value={form.sourceOfFunds} onChange={(e) => update('sourceOfFunds', e.target.value)} /></div>
            <label className="flex gap-3 items-start text-sm"><input type="checkbox" checked={form.pepDeclared} onChange={(e) => update('pepDeclared', e.target.checked)} className="mt-1" /><span>Declaro que sou Pessoa Exposta Politicamente (PEP) ou possuo condição equivalente.</span></label>
            <label className="flex gap-3 items-start text-sm"><input type="checkbox" checked={form.termsAccepted} onChange={(e) => update('termsAccepted', e.target.checked)} className="mt-1" /><span>Confirmo que as informações são verdadeiras e autorizo o tratamento dos dados para identificação, KYC, prevenção a fraudes e cumprimento de obrigações legais.</span></label>
            <div className="rounded-lg border bg-muted/40 p-4 text-sm text-muted-foreground">Nesta versão Sandbox, validação documental e de identidade serão analisadas manualmente. O sistema não considera selfie ou documento automaticamente aprovados.</div>
          </div>}

          <div className="flex justify-between pt-2">
            <Button variant="outline" disabled={step === 1 || submitting} onClick={() => setStep((s) => s - 1)}>Voltar</Button>
            {step < 3 ? <Button onClick={() => setStep((s) => s + 1)}>Continuar</Button> : <Button disabled={submitting || !form.termsAccepted} onClick={submit}>{submitting ? 'Enviando...' : 'Enviar para análise KYC'}</Button>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default KycOnboarding;
